import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useNavigate } from "react-router-dom";
import { usePageContext } from "../../context/PageContext";
import { useChatbots } from "../../hooks/useChatbots";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import GenericTable from "../../components/GenericTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useTranslation } from "react-i18next";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
}

const ChatbotsList = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { setCurrentPage } = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canCreate = can(scopeSet, "chatbot", "create");
  const canUpdate = can(scopeSet, "chatbot", "update");
  const canDelete = can(scopeSet, "chatbot", "delete");

  const {
    data: botUsers = [],
    error,
    refetch,
    deleteMutation,
    updateMutation,
    isLoading,
    isFetching,
  } = useChatbots();

  useEffect(() => {
    setCurrentPage({ title: t("chatbotConfigPage.title"), showBackButton: false });
  }, [setCurrentPage, t]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error?.message || t("chatbotsListPage.loadError"));
    } else {
      setErrorMessage(null);
    }
  }, [error, t]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return botUsers;
    return botUsers.filter((u) => {
      const nome = String(u?.nome ?? "").toLowerCase();
      const email = String(u?.email ?? "").toLowerCase();
      return nome.includes(q) || email.includes(q);
    });
  }, [botUsers, search]);

  const rows = useMemo(
    () =>
      filtered.map((u) => {
        const st = u.status ?? u.ativo;
        const active = st === 1 || st === true || st === "1";
        return {
          id: u.id,
          name: u.nome ?? "—",
          status: active,
          updatedAt: u.updatedAt ?? u.updated_at ?? u.data_ultima_alteracao,
        };
      }),
    [filtered]
  );

  const handleToggleStatus = useCallback(
    (row) => {
      const nextActive = !row.status;
      updateMutation.mutate(
        { userId: row.id, active: nextActive },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: t("agentsListPage.snackStatusUpdated") });
            refetch();
          },
          onError: (err) => {
            setSnack({
              open: true,
              severity: "error",
              message: err?.message || t("agentsListPage.snackStatusError"),
            });
          },
        }
      );
    },
    [updateMutation, refetch, t]
  );

  const columns = useMemo(
    () => [
      { field: "name", headerName: t("shared.name"), flex: 1, minWidth: 200 },
      {
        field: "status",
        headerName: t("shared.status"),
        width: 160,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={!!params.row.status}
              disabled={!canUpdate || updateMutation.isLoading}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleStatus(params.row);
              }}
            />
            <Chip
              size="small"
              label={params.row.status ? t("shared.active") : t("shared.inactive")}
              color={params.row.status ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        ),
      },
      {
        field: "updatedAt",
        headerName: t("chatbotsListPage.columnLastUpdate"),
        width: 180,
        renderCell: (p) => formatDate(p.row.updatedAt),
      },
      {
        field: "actions",
        headerName: t("shared.actions"),
        width: 140,
        renderCell: (p) => (
          <Box display="flex" gap={0.5}>
            <Tooltip title={t("agentsListPage.analytics")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/chatbots/${p.row.id}/analytics`);
                }}
              >
                <BarChartRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canUpdate && (
              <Tooltip title={t("shared.edit")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/chatbots/${p.row.id}`);
                  }}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t("shared.delete")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(p.row.id);
                  }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, canUpdate, canDelete, navigate, handleToggleStatus, updateMutation.isLoading]
  );

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, severity: "success", message: t("chatbotsListPage.snackDeleted") });
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setSnack({
          open: true,
          severity: "error",
          message: err?.message || t("chatbotsListPage.snackDeleteError"),
        });
      },
    });
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? t("chatbotsListPage.snackBulkDeleteAllError")
          : t("chatbotsListPage.snackBulkDeletePartial", { failed: failed.length, total: ids.length })
      );
      setSnack({
        open: true,
        severity: "error",
        message:
          failed.length === ids.length
            ? t("chatbotsListPage.snackDeleteFailed")
            : t("chatbotsListPage.snackSomeDeletesFailed"),
      });
    } else {
      setSnack({ open: true, severity: "success", message: t("chatbotsListPage.snackBulkSuccess") });
    }
    refetch();
  };

  return (
    <Box className="tour-chatbots-root" sx={{ paddingTop: 1 }}>
      <Box
        className="tour-chatbots-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 1,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("chatbotsListPage.heading")}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/chatbots/new")}
            fullWidth={isMobile}
          >
            {t("chatbotsListPage.createButton")}
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Paper className="tour-chatbots-table">
        <GenericTable
          rows={rows}
          columns={columns}
          rowCount={rows.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
          onSearch={(term) => {
            setSearch(term);
            setPage(0);
          }}
          isLoading={isLoading || isFetching}
          onExportPDFTitle={t("chatbotsListPage.exportSelectedTitle")}
          exportFileName="chatbots"
          onDeleteSelected={canDelete ? handleDeleteSelected : undefined}
          onRowClick={(row) => navigate(`/chatbots/${row.id}`)}
          paginationMode="client"
        />
      </Paper>

      <ConfirmDialog
        open={!!confirmDelete}
        title={t("shared.deleteConfirmation")}
        content={
          deleteMutation.isLoading ? t("shared.processingDelete") : t("chatbotsListPage.deleteQuestion")
        }
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
        confirmText={t("shared.delete")}
        confirmColor="error"
        loading={deleteMutation.isLoading}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default ChatbotsList;
